// frontend/src/pages/UserManagementPage.js
import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Select, Typography, Popconfirm, Space, Avatar, Tooltip } from 'antd'; // Added Tooltip
import { PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, UserOutlined } from '@ant-design/icons'; // Added InfoCircleOutlined, UserOutlined
import { getAllUsers, createUser, deleteUser, updateUser } from '../services/userService';
import api, { imageBaseUrl } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const { Title } = Typography;
const { Option } = Select;

// --- NEW: Password Validation Rules ---
const passwordRules = [
    { required: true, message: 'Please set a password!' },
    { min: 8, message: 'Minimum 8 characters.' },
    { pattern: /[A-Z]/, message: 'Must contain one uppercase letter.' },
    { pattern: /[a-z]/, message: 'Must contain one lowercase letter.' },
    { pattern: /[0-9]/, message: 'Must contain one number.' },
    {
        validator: (_, value) => {
            if (!value || /[!@#$%^&*()]/.test(value)) {
                return Promise.resolve();
            }
            return Promise.reject('Must contain one special character (!@#$%^&*()).');
        }
    }
];

const UserManagementPage = () => {
    const { message } = useNotification();
    const { isMobile } = useResponsive();
    const { user: currentUser } = useAuth(); // Get the currently logged-in user
    const [users, setUsers] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();
    const imageVersion = React.useMemo(() => Date.now(), []);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const userData = await getAllUsers();
            console.debug('Fetched users:', userData);
            // Sort users by role (principal, then teacher), then alphabetically by username
            const roleOrder = {
                'principal': 1,
                'teacher': 2,
            };
            const sortedUserData = userData.sort((a, b) => {
                const orderA = roleOrder[a.role] || 99; // Assign order, with a default for other roles
                const orderB = roleOrder[b.role] || 99;

                if (orderA !== orderB) {
                    return orderA - orderB; // Sort by role
                }
                // If roles are the same, sort by username
                return a.username.localeCompare(b.username);
            });
            setUsers(sortedUserData.map(u => ({ ...u, key: u.id })));
        } catch (error) {
            message.error('Failed to load users.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingUser(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        // Protect the special 'developer' account from being edited
        if (record.username === 'developer') {
            message.error("The 'developer' account cannot be edited.");
            return;
        }
        setEditingUser(record);
        form.setFieldsValue({
            username: record.username,
            role: record.role,
        });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingUser(null);
    };

    const handleOk = () => {
        // Additional safety: block saving changes to protected account if somehow reached
        if (editingUser && editingUser.username === 'developer') {
            message.error("The 'developer' account cannot be edited.");
            return;
        }

        form.validateFields().then(async (values) => {
            try {
                if (editingUser) {
                    // Note: Backend updateUser route needs to exist and handle this
                    await updateUser(editingUser.id, values);
                    message.success('User updated successfully!');
                } else {
                    await createUser(values);
                    message.success('User created successfully!');
                }
                handleCancel();
                fetchUsers();
            } catch (error) {
                message.error(error.response?.data?.error || 'Failed to save user.');
            }
        });
    };

    const handleDelete = async (id) => {
        // Ensure the protected 'developer' account cannot be deleted
        const userRecord = users.find(u => u.id === id);
        if (userRecord && userRecord.username === 'developer') {
            message.error("The 'developer' account cannot be deleted.");
            return;
        }
        try {
            await deleteUser(id);
            message.success('User deleted successfully!');
            fetchUsers();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to delete user.');
        }
    };

    const handleImpersonate = async (record) => {
        try {
            const response = await api.post('/auth/impersonate', { userId: record.id });
            const { token } = response.data;
            
            // Save original super admin token
            localStorage.setItem('adminToken', localStorage.getItem('token'));
            // Switch to target user token
            localStorage.setItem('token', token);
            
            message.success(`Now impersonating ${record.full_name || record.username}`);
            setTimeout(() => {
                window.location.href = '/';
            }, 1000);
        } catch (error) {
            console.error('Impersonation error:', error);
            message.error(error.response?.data?.error || 'Failed to impersonate user.');
        }
    };

    const columns = [
        {
            title: 'Avatar',
            dataIndex: 'profile_picture_url',
            key: 'avatar',
            align: 'center',
            width: 64,
            render: (profile_picture_url, record) => {
                const src = profile_picture_url ? `${imageBaseUrl}${profile_picture_url}?cb=${imageVersion}` : `${imageBaseUrl}/uploads/profile_pictures/default.png`;
                return (
                    <Avatar
                        src={src}
                        alt={record.username}
                        style={{ backgroundColor: '#87d068', verticalAlign: 'middle' }}
                    >
                        {!profile_picture_url && record.username?.[0]?.toUpperCase()}
                    </Avatar>
                );
            },
        },
        { title: 'Username', dataIndex: 'username', key: 'username' },
        { title: 'Name', dataIndex: 'full_name', key: 'full_name' },
        { title: 'Phone', dataIndex: 'phone_number', key: 'phone_number' },
        { title: 'Role', dataIndex: 'role', key: 'role' },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <Space>
                    {/* Disable edit for the 'developer' account */}
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        disabled={record.username === 'developer'}
                    />
                    {currentUser.role === 'super_admin' && record.id !== currentUser.id && record.role !== 'super_admin' && (
                        <Tooltip title="Impersonate User">
                            <Button
                                icon={<UserOutlined />}
                                onClick={() => handleImpersonate(record)}
                                style={{ backgroundColor: '#722ed1', color: 'white', border: 'none' }}
                            />
                        </Tooltip>
                    )}
                    {/* Prevent deleting own account and protect the 'developer' account */}
                    {record.id !== currentUser.id && record.username !== 'developer' && (
                        <Popconfirm okType='default' title="Are you sure to delete this user?" onConfirm={() => handleDelete(record.id)}>
                            <Button icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <Title level={2} style={{ margin: 0 }}>User Management</Title>
                <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    block={isMobile}
                >
                    Add New User
                </Button>
            </Space>
            <Table
                dataSource={users}
                columns={columns}
                loading={loading}
                bordered
                scroll={{ x: 'max-content' }}
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onShowSizeChange: (current, size) => setPageSize(size),
                    onChange: (page, size) => setPageSize(size),
                    responsive: true,
                    showTotal: (total) => `Total ${total} users`
                }}
            />
            <Modal
                okText='Save'
                okType='default'
                title={editingUser ? "Edit User" : "Add New User"}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                        <Input disabled={!!editingUser} />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={
                            <Space>
                                Password
                                <Tooltip title="Must be 8+ chars, include uppercase, lowercase, number, and one special character (!@#$%^&*()).">
                                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                                </Tooltip>
                            </Space>
                        }
                        rules={editingUser ? [] : passwordRules} // Required only for new users
                        help={editingUser ? "Leave blank to keep existing password." : null}
                    >
                        <Input.Password placeholder={editingUser ? "New password (optional)" : "Set a temporary password"} />
                    </Form.Item>

                    <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                        <Select placeholder="Select a role">
                            <Option value="teacher">Teacher</Option>
                            <Option value="principal">Principal</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagementPage;